import {
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  TextField,
} from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Route, Routes } from 'react-router-dom';
import { toast } from 'react-toastify';

import UserCard from '../../../components/global/cards/userCard/UserCard';
import Loading from '../../../components/global/loading/Loading';
import { useUser } from '../../../context/UserContext';
import translateUserStatus from '../../../helperFunctions/translateUserStatus';
import { useDebounce } from '../../../hooks/useDebounce';
import { User as UserModel, UserStatus } from '../../../models/User';
import User from './user/User';

function Users() {
  const { control } = useForm();

  const { searchUsers } = useUser();

  const [users, setUsers] = React.useState<UserModel[]>([]);
  const [totalUsers, setTotalUsers] = React.useState(0);
  const [query, setQuery] = React.useState('');
  const [userStatus, setUserStatus] = React.useState<UserStatus | 'all'>('all');
  const [limit, setLimit] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);

  const debouncedQuery = useDebounce(query, 500);

  const getUsersData = useCallback(
    async (
      page: number,
      query: string,
      userStatus: UserStatus | 'all',
      limit: number,
    ) => {
      try {
        const { users, totalPages, totalUsers } = await searchUsers(
          page,
          limit,
          query,
          userStatus === 'all' ? undefined : userStatus,
        );
        setUsers(users);
        setTotalPages(totalPages);
        setTotalUsers(totalUsers);
        setCurrentPage(page);
      } catch (error: any) {
        toast.error(error.message || 'Wystąpił błąd podczas pobierania danych');
      }
    },
    [searchUsers],
  );

  useEffect(() => {
    getUsersData(currentPage, debouncedQuery, userStatus, limit);
  }, [debouncedQuery, userStatus, limit, getUsersData, currentPage]);

  return (
    <div>
      <Routes>
        <Route
          path="/"
          element={
            <div>
              <div className="space-y-2">
                <h3 className="text-2xl">Lista użytkowników</h3>
                {/* Search input */}
                <Controller
                  name="search"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Szukaj użytkownika (email, imię, nazwisko)"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  )}
                />

                {/* Payment Status filter */}
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Status konta</InputLabel>
                  <Select
                    value={userStatus}
                    onChange={(e) =>
                      setUserStatus(e.target.value as UserStatus)
                    }
                    label="Status płatności"
                  >
                    <MenuItem value="all">Wszystkie</MenuItem>
                    {Object.values(UserStatus).map((status) => (
                      <MenuItem key={status} value={status}>
                        {translateUserStatus(status)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Limit filter */}
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Limit</InputLabel>
                  <Select
                    value={limit}
                    onChange={(e) => setLimit(Number(e.target.value))}
                    label="Limit"
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div>
                {users === undefined && <Loading />}

                {users && (
                  <div className="space-y-2">
                    {/* Display order count */}
                    <p>
                      Wyświetlono {users.length} z {totalUsers} użytkowników
                    </p>

                    <div className="flex flex-col space-y-2">
                      {users.map((user) => (
                        <UserCard key={user.id} user={user} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Pagination */}
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) =>
                  getUsersData(page, query, userStatus, limit)
                }
                color="primary"
              />
            </div>
          }
        />
        <Route path="/:id" element={<User />} />
      </Routes>
    </div>
  );
}

export default Users;
